// No prototype reference — follows design system.
// Operation close confirmation modal — FOP-S5.
// Fetches close-summary, shows impact rows, optional force-close checkbox.

import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import { useVenues } from '../hooks/useVenues';
import { get, post } from '@vigilo/api-client';
import { useToast, ToastContainer } from './Toast';
import {
  Btn, Icons,
  FONT_SIZE, FONT_WEIGHT, RADIUS, SIZE, SPACE,
  type ColorPalette,
} from '@vigilo/design-system';
import { getCrowdC } from '../../design/crowdPalette';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CloseSummary {
  openAlertCount:      number;
  escalatedAlertCount: number;
  temporaryZoneCount:  number;
  permanentZoneCount:  number;
}

const MOCK_CLOSE_SUMMARY: CloseSummary = {
  openAlertCount:      2,
  escalatedAlertCount: 0,
  temporaryZoneCount:  0,
  permanentZoneCount:  6,
};

// ── ImpactRow ─────────────────────────────────────────────────────────────────

function ImpactRow({ C, icon, iconColor, label, count, countColor }: {
  C:          ColorPalette;
  icon:       ReactNode;
  iconColor:  string;
  label:      string;
  count:      number;
  countColor: string;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: SPACE[12],
      padding: `${SPACE[10]}px ${SPACE[12]}px`,
      borderRadius: RADIUS.card,
      background: C.surface, border: `1px solid ${C.border}`,
    }}>
      <span style={{ color: iconColor, flexShrink: 0, display: 'flex' }}>{icon}</span>
      <span style={{ flex: 1, fontSize: FONT_SIZE.body, color: C.sub }}>{label}</span>
      {count > 0 ? (
        <span style={{
          fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
          color: countColor, background: `${countColor}22`,
          padding: `${SPACE[1]}px ${SPACE[6]}px`, borderRadius: RADIUS.tab,
          fontVariantNumeric: 'tabular-nums',
        }}>{count}</span>
      ) : (
        <span style={{ fontSize: FONT_SIZE.xs, color: C.muted, fontStyle: 'italic' }}>None</span>
      )}
    </div>
  );
}

// ── CloseOperationModal ───────────────────────────────────────────────────────

export function CloseOperationModal() {
  const navigate = useNavigate();
  const {
    theme, selectedOperation, activeOperations,
    setSelectedOperation, removeActiveOperation, closeCloseModal,
  } = useApp();
  const C = getCrowdC(theme);
  const { isMock } = useMockMode();
  const { venues } = useVenues();
  const { toasts, show: showToast, dismiss } = useToast();

  const [summary,    setSummary]    = useState<CloseSummary | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [force,      setForce]      = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inlineErr,  setInlineErr]  = useState<string | null>(null);
  const [succeeded,  setSucceeded]  = useState(false);

  const operation = selectedOperation;

  useEffect(() => {
    if (!operation) return;
    if (isMock) {
      const t = setTimeout(() => { setSummary(MOCK_CLOSE_SUMMARY); setLoading(false); }, 300);
      return () => clearTimeout(t);
    }
    get<CloseSummary | { data?: CloseSummary }>(`/operations/${operation.id}/close-summary`)
      .then(body => {
        const b = body as { data?: CloseSummary };
        setSummary(b.data !== undefined ? b.data : body as CloseSummary);
      })
      .catch(() => {
        showToast('Failed to load close summary — check Core API.', 'error');
        closeCloseModal();
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation?.id]);

  async function handleConfirm() {
    if (!operation || !summary) return;
    setSubmitting(true);
    setInlineErr(null);
    try {
      if (isMock) {
        await new Promise(r => setTimeout(r, 800));
      } else {
        await post(`/operations/${operation.id}/close`, { force });
      }
      // Remove from active list and clear selection
      const remaining = activeOperations.filter(op => op.id !== operation.id);
      removeActiveOperation(operation.id);
      setSelectedOperation(remaining[0] ?? null);
      setSucceeded(true);
      setTimeout(() => {
        closeCloseModal();
        navigate('/operations');
      }, 1500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.toLowerCase().includes('escalat')) {
        setInlineErr('Escalated alerts must be resolved first, or enable Force Close.');
      } else {
        showToast('Failed to close operation — check Core API.', 'error');
        closeCloseModal();
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!operation) return null;

  const isPaused     = operation.status === 'paused';
  const venueName    = venues.find(v => v.id === operation.venueId)?.name ?? 'Unknown venue';
  const opDate       = new Date(operation.scheduledStart).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
  const hasEscalated = (summary?.escalatedAlertCount ?? 0) > 0;
  const confirmDisabled = submitting || loading || (hasEscalated && !force);
  const confirmEmber    = hasEscalated && force;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 3000, fontFamily: "'Albert Sans', sans-serif",
    }}>
      <div style={{
        background: C.panel, borderRadius: RADIUS.panel, border: `1px solid ${C.border2}`,
        width: 480, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: `${SPACE[20]}px ${SPACE[24]}px ${SPACE[16]}px`,
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'flex-start', gap: SPACE[14],
        }}>
          <div style={{
            width: SIZE.avatarMd, height: SIZE.avatarMd, borderRadius: RADIUS.card,
            background: C.emberDim, border: `1px solid ${C.ember}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.ember, flexShrink: 0,
          }}>
            <Icons.Alert size={SIZE.iconMd} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold,
              color: C.text, marginBottom: SPACE[3],
            }}>
              {isPaused ? 'Close Paused Operation' : 'Close Operation'}
            </div>
            <div style={{ fontSize: FONT_SIZE.body, color: C.ember }}>
              This action cannot be undone.
            </div>
          </div>
          <button
            onClick={closeCloseModal}
            disabled={submitting}
            style={{
              background: 'none', border: 'none', color: C.muted,
              cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0,
            }}
          >×</button>
        </div>

        {/* ── Body ── */}
        <div style={{
          padding: `${SPACE[20]}px ${SPACE[24]}px`,
          display: 'flex', flexDirection: 'column', gap: SPACE[14],
        }}>

          {/* Success state */}
          {succeeded ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: SPACE[12], padding: `${SPACE[24]}px 0`,
              textAlign: 'center',
            }}>
              <div style={{
                width: SIZE.stepCircle, height: SIZE.stepCircle, borderRadius: '50%',
                background: C.greenDim, border: `1px solid ${C.green}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: C.green,
              }}>
                <Icons.Check size={SIZE.iconMd} />
              </div>
              <div style={{ fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
                Operation closed
              </div>
              <div style={{ fontSize: FONT_SIZE.body, color: C.muted }}>
                Alerts and zones archived.
              </div>
            </div>
          ) : (
            <>
              {/* Operation summary card */}
              <div style={{
                background: C.surface, border: `1px solid ${C.border2}`,
                borderRadius: RADIUS.card, padding: `${SPACE[12]}px ${SPACE[14]}px`,
              }}>
                <div style={{
                  fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold,
                  color: C.text, marginBottom: SPACE[2],
                }}>
                  {operation.name}
                </div>
                <div style={{ fontSize: FONT_SIZE.body, color: C.muted }}>
                  {venueName} · {opDate}
                </div>
              </div>

              {/* Impact rows */}
              {loading ? (
                <div style={{
                  padding: '20px 0', textAlign: 'center',
                  color: C.muted, fontSize: FONT_SIZE.body,
                }}>
                  Loading close summary…
                </div>
              ) : summary && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[6] }}>
                  <ImpactRow
                    C={C}
                    icon={<Icons.Bell size={SIZE.icon} />}
                    iconColor={C.amber}
                    label="Alerts will be archived with this operation"
                    count={summary.openAlertCount}
                    countColor={C.amber}
                  />
                  <ImpactRow
                    C={C}
                    icon={<Icons.Alert size={SIZE.icon} />}
                    iconColor={C.ember}
                    label="Escalated alerts require attention"
                    count={summary.escalatedAlertCount}
                    countColor={C.ember}
                  />

                  {/* Escalated warning + force checkbox */}
                  {hasEscalated && (
                    <div style={{
                      background: C.emberDim, border: `1px solid ${C.ember}44`,
                      borderRadius: RADIUS.card, padding: `${SPACE[12]}px ${SPACE[14]}px`,
                      display: 'flex', flexDirection: 'column', gap: SPACE[10],
                    }}>
                      <div style={{
                        fontSize: FONT_SIZE.base, color: C.ember, lineHeight: '1.5',
                      }}>
                        Resolve escalated alerts before closing, or force close to archive them with a system note.
                      </div>
                      <label style={{
                        display: 'flex', alignItems: 'flex-start',
                        gap: SPACE[8], cursor: 'pointer',
                      }}>
                        <input
                          type="checkbox"
                          checked={force}
                          onChange={e => setForce(e.target.checked)}
                          style={{ marginTop: 2, accentColor: C.ember, flexShrink: 0 }}
                        />
                        <span style={{ fontSize: FONT_SIZE.base, color: C.text }}>
                          Force close - archive escalated alerts with a system note
                        </span>
                      </label>
                    </div>
                  )}

                  <ImpactRow
                    C={C}
                    icon={<Icons.Archive size={SIZE.icon} />}
                    iconColor={C.muted}
                    label="Temporary zones will be archived"
                    count={summary.temporaryZoneCount}
                    countColor={C.muted}
                  />
                  <ImpactRow
                    C={C}
                    icon={<Icons.Pin size={SIZE.icon} />}
                    iconColor={C.teal}
                    label={`Permanent zones will remain active at ${venueName}`}
                    count={summary.permanentZoneCount}
                    countColor={C.teal}
                  />
                </div>
              )}

              {/* Inline error */}
              {inlineErr && (
                <div style={{
                  background: C.emberDim, border: `1px solid ${C.ember}`,
                  borderRadius: RADIUS.sm, padding: `${SPACE[10]}px ${SPACE[12]}px`,
                  fontSize: FONT_SIZE.base, color: C.ember,
                }}>{inlineErr}</div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        {!succeeded && (
          <div style={{
            padding: `${SPACE[14]}px ${SPACE[24]}px`,
            borderTop: `1px solid ${C.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Btn C={C} variant="ghost" onClick={closeCloseModal} disabled={submitting}>
              Cancel
            </Btn>
            <Btn
              C={C}
              variant="ghost"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              style={{
                background:  confirmEmber ? C.ember : 'transparent',
                borderColor: C.ember + '88',
                color:       confirmEmber ? '#fff' : C.ember,
              }}
            >
              {submitting ? 'Closing…' : 'Close Operation'}
            </Btn>
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} onDismiss={dismiss} C={C} />
    </div>
  );
}
